import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Visitors
export const getVisitors = async () => {
  const response = await axios.get(`${API_BASE}/visitors`, { headers: getAuthHeader() });
  return response.data;
};

export const createVisitor = async (visitorData) => {
  const response = await axios.post(`${API_BASE}/visitors`, visitorData, { headers: getAuthHeader() });
  return response.data;
};

export const updateVisitorStatus = async (visitorId, status) => {
  const response = await axios.patch(`${API_BASE}/visitors/${visitorId}`, { status }, { headers: getAuthHeader() });
  return response.data;
};

export const getVisitorByCode = async (visitorId) => {
  const response = await axios.get(`${API_BASE}/visitors/code/${visitorId}`);
  return response.data;
};

// Complaints
export const getComplaints = async () => {
  const response = await axios.get(`${API_BASE}/complaints`, { headers: getAuthHeader() });
  return response.data;
};

export const createComplaint = async (complaintData, files = []) => {
  // If no files, use simple JSON endpoint
  if (!files || files.length === 0) {
    const response = await axios.post(`${API_BASE}/complaints`, complaintData, { headers: getAuthHeader() });
    return response.data;
  }
  
  // With files, use multipart form data
  const formData = new FormData();
  formData.append('title', complaintData.title);
  formData.append('description', complaintData.description);
  formData.append('category', complaintData.category);
  formData.append('priority', complaintData.priority || 'medium');
  
  files.forEach(file => {
    formData.append('files', file);
  });
  
  const response = await axios.post(`${API_BASE}/complaints/with-files`, formData, {
    headers: {
      ...getAuthHeader(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const updateComplaintStatus = async (complaintId, status) => {
  const response = await axios.patch(`${API_BASE}/complaints/${complaintId}`, { status }, { headers: getAuthHeader() });
  return response.data;
};

// Amenities & Bookings
export const getAmenities = async () => {
  const response = await axios.get(`${API_BASE}/amenities`, { headers: getAuthHeader() });
  return response.data;
};

export const getBookings = async () => {
  const response = await axios.get(`${API_BASE}/bookings`, { headers: getAuthHeader() });
  return response.data;
};

export const createBooking = async (bookingData) => {
  const response = await axios.post(`${API_BASE}/bookings`, bookingData, { headers: getAuthHeader() });
  return response.data;
};

// Bills
export const getBills = async () => {
  const response = await axios.get(`${API_BASE}/bills`, { headers: getAuthHeader() });
  return response.data;
};

export const getMyBills = async () => {
  const response = await axios.get(`${API_BASE}/bills/my`, { headers: getAuthHeader() });
  return response.data;
};

export const payBill = async (billId) => {
  const response = await axios.post(`${API_BASE}/bills/pay`, { bill_id: billId }, { headers: getAuthHeader() });
  return response.data;
};

// Announcements
export const getAnnouncements = async () => {
  const response = await axios.get(`${API_BASE}/announcements`, { headers: getAuthHeader() });
  return response.data;
};

export const createAnnouncement = async (announcementData) => {
  const response = await axios.post(`${API_BASE}/announcements`, announcementData, { headers: getAuthHeader() });
  return response.data;
};

// Chat
export const sendChatMessage = async (message, sessionId = null) => {
  const response = await axios.post(`${API_BASE}/chat`, { message, session_id: sessionId }, { headers: getAuthHeader() });
  return response.data;
};

export const getChatHistory = async (sessionId) => {
  const response = await axios.get(`${API_BASE}/chat/history/${sessionId}`, { headers: getAuthHeader() });
  return response.data;
};

// Dashboard Stats
export const getDashboardStats = async () => {
  const response = await axios.get(`${API_BASE}/dashboard/stats`, { headers: getAuthHeader() });
  return response.data;
};

// Users (Admin only)
export const getAllUsers = async () => {
  const response = await axios.get(`${API_BASE}/users`, { headers: getAuthHeader() });
  return response.data;
};

export const createUser = async (userData) => {
  const response = await axios.post(`${API_BASE}/users/create`, userData, { headers: getAuthHeader() });
  return response.data;
};

// Bills (Admin)
export const createBillForUser = async (billData) => {
  const response = await axios.post(`${API_BASE}/bills/create/user`, billData, { headers: getAuthHeader() });
  return response.data;
};

export const createBillForAllResidents = async (billData) => {
  const response = await axios.post(`${API_BASE}/bills/create/all`, billData, { headers: getAuthHeader() });
  return response.data;
};

export const payBillsByUnit = async (unitNumber, amount) => {
  const response = await axios.post(`${API_BASE}/bills/pay-by-unit`, { unit_number: unitNumber, amount }, { headers: getAuthHeader() });
  return response.data;
};

export const getBillSummary = async () => {
  const response = await axios.get(`${API_BASE}/bills/summary`, { headers: getAuthHeader() });
  return response.data;
};

// Amenities (Admin)
export const createAmenity = async (amenityData) => {
  const response = await axios.post(`${API_BASE}/amenities/create`, amenityData, { headers: getAuthHeader() });
  return response.data;
};

export const updateAmenity = async (amenityId, updateData) => {
  const response = await axios.patch(`${API_BASE}/amenities/${amenityId}`, updateData, { headers: getAuthHeader() });
  return response.data;
};

export const deleteAmenity = async (amenityId) => {
  const response = await axios.delete(`${API_BASE}/amenities/${amenityId}`, { headers: getAuthHeader() });
  return response.data;
};

// Analytics
export const getRealAnalytics = async () => {
  const response = await axios.get(`${API_BASE}/analytics/real`, { headers: getAuthHeader() });
  return response.data;
};

// Seed Data
export const seedData = async () => {
  const response = await axios.post(`${API_BASE}/seed`);
  return response.data;
};

// AI Rewrite/Generate
export const rewriteComplaint = async (title, description, category) => {
  const response = await axios.post(`${API_BASE}/ai/rewrite-complaint`, { title, description, category }, { headers: getAuthHeader() });
  return response.data;
};

export const generateNotice = async (instruction, priority = 'normal') => {
  const response = await axios.post(`${API_BASE}/ai/generate-notice`, { instruction, priority }, { headers: getAuthHeader() });
  return response.data;
};

export const generateAmenity = async (description) => {
  const response = await axios.post(`${API_BASE}/ai/generate-amenity`, { description }, { headers: getAuthHeader() });
  return response.data;
};

// Booking Management
export const getTodaysBookings = async () => {
  const response = await axios.get(`${API_BASE}/bookings/today`, { headers: getAuthHeader() });
  return response.data;
};

export const getMyBookings = async () => {
  const response = await axios.get(`${API_BASE}/bookings/my`, { headers: getAuthHeader() });
  return response.data;
};

export const bookAmenity = async (bookingData) => {
  const response = await axios.post(`${API_BASE}/bookings`, bookingData, { headers: getAuthHeader() });
  return response.data;
};

export const cancelBooking = async (bookingId) => {
  const response = await axios.delete(`${API_BASE}/bookings/${bookingId}`, { headers: getAuthHeader() });
  return response.data;
};

export const updateBookingStatus = async (bookingId, status) => {
  const response = await axios.patch(`${API_BASE}/bookings/${bookingId}/status`, { status }, { headers: getAuthHeader() });
  return response.data;
};

export const lookupBookingByCode = async (bookingCode) => {
  const response = await axios.get(`${API_BASE}/bookings/lookup/${bookingCode}`, { headers: getAuthHeader() });
  return response.data;
};

export const getBookingStats = async () => {
  const response = await axios.get(`${API_BASE}/bookings/stats`, { headers: getAuthHeader() });
  return response.data;
};

// ============ FORUM API ============
export const getForumPosts = async (category = null) => {
  const url = category && category !== 'all' 
    ? `${API_BASE}/forum/posts?category=${category}`
    : `${API_BASE}/forum/posts`;
  const response = await axios.get(url, { headers: getAuthHeader() });
  return response.data;
};

export const getForumPost = async (postId) => {
  const response = await axios.get(`${API_BASE}/forum/posts/${postId}`, { headers: getAuthHeader() });
  return response.data;
};

export const createForumPost = async (postData) => {
  const response = await axios.post(`${API_BASE}/forum/posts`, postData, { headers: getAuthHeader() });
  return response.data;
};

export const createForumReply = async (postId, content) => {
  const response = await axios.post(`${API_BASE}/forum/posts/${postId}/reply`, { content }, { headers: getAuthHeader() });
  return response.data;
};

export const voteOnPoll = async (postId, option) => {
  const response = await axios.post(`${API_BASE}/forum/posts/${postId}/vote?option=${encodeURIComponent(option)}`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const togglePinPost = async (postId) => {
  const response = await axios.patch(`${API_BASE}/forum/posts/${postId}/pin`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const deleteForumPost = async (postId) => {
  const response = await axios.delete(`${API_BASE}/forum/posts/${postId}`, { headers: getAuthHeader() });
  return response.data;
};

// ============ EVENTS API ============
export const getEvents = async () => {
  const response = await axios.get(`${API_BASE}/events`, { headers: getAuthHeader() });
  return response.data;
};

export const getEvent = async (eventId) => {
  const response = await axios.get(`${API_BASE}/events/${eventId}`, { headers: getAuthHeader() });
  return response.data;
};

export const createEvent = async (eventData) => {
  const response = await axios.post(`${API_BASE}/events`, eventData, { headers: getAuthHeader() });
  return response.data;
};

export const updateEvent = async (eventId, updateData) => {
  const response = await axios.patch(`${API_BASE}/events/${eventId}`, updateData, { headers: getAuthHeader() });
  return response.data;
};

export const deleteEvent = async (eventId) => {
  const response = await axios.delete(`${API_BASE}/events/${eventId}`, { headers: getAuthHeader() });
  return response.data;
};

export const registerForEvent = async (eventId) => {
  const response = await axios.post(`${API_BASE}/events/${eventId}/register`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const unregisterFromEvent = async (eventId) => {
  const response = await axios.post(`${API_BASE}/events/${eventId}/unregister`, {}, { headers: getAuthHeader() });
  return response.data;
};

// ============ OCCUPANCY NOTIFICATIONS ============
export const getOccupancyNotifications = async () => {
  const response = await axios.get(`${API_BASE}/occupancy/notifications`, { headers: getAuthHeader() });
  return response.data;
};

export const createOccupancyNotification = async (data) => {
  const response = await axios.post(`${API_BASE}/occupancy/notifications`, data, { headers: getAuthHeader() });
  return response.data;
};

export const processOccupancyNotification = async (notificationId, action, remarks = null) => {
  const response = await axios.patch(
    `${API_BASE}/occupancy/notifications/${notificationId}?action=${action}${remarks ? `&remarks=${encodeURIComponent(remarks)}` : ''}`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

// ============ TENANT MANAGEMENT ============
export const getTenants = async () => {
  const response = await axios.get(`${API_BASE}/tenants`, { headers: getAuthHeader() });
  return response.data;
};

export const getTenantLeases = async () => {
  const response = await axios.get(`${API_BASE}/tenants/leases`, { headers: getAuthHeader() });
  return response.data;
};

// ============ RENT PAYMENTS ============
export const getRentPayments = async () => {
  const response = await axios.get(`${API_BASE}/rent/payments`, { headers: getAuthHeader() });
  return response.data;
};

export const getRentSummary = async () => {
  const response = await axios.get(`${API_BASE}/rent/summary`, { headers: getAuthHeader() });
  return response.data;
};

export const payRent = async (paymentId) => {
  const response = await axios.post(`${API_BASE}/rent/pay/${paymentId}`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const confirmRentReceipt = async (paymentId) => {
  const response = await axios.post(`${API_BASE}/rent/confirm/${paymentId}`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const generateMonthlyRent = async () => {
  const response = await axios.post(`${API_BASE}/rent/generate-monthly`, {}, { headers: getAuthHeader() });
  return response.data;
};

// ============ ADDITIONAL TENANT MANAGEMENT ============
export const removeTenant = async (tenantId, reason = '') => {
  const response = await axios.post(`${API_BASE}/tenants/${tenantId}/remove`, { reason }, { headers: getAuthHeader() });
  return response.data;
};

export const approveTenantRemoval = async (tenantId) => {
  const response = await axios.post(`${API_BASE}/tenants/${tenantId}/approve-removal`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const rejectTenantRemoval = async (tenantId) => {
  const response = await axios.post(`${API_BASE}/tenants/${tenantId}/reject-removal`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const notifyTenantVacate = async (tenantId, reason) => {
  const response = await axios.post(`${API_BASE}/tenants/${tenantId}/notify-vacate`, { reason }, { headers: getAuthHeader() });
  return response.data;
};

export const approveTenantVacancy = async (tenantId, adminNote = null) => {
  const response = await axios.post(`${API_BASE}/tenants/${tenantId}/approve-vacancy`, { admin_note: adminNote }, { headers: getAuthHeader() });
  return response.data;
};

export const rejectTenantVacancy = async (tenantId, adminNote = null) => {
  const response = await axios.post(`${API_BASE}/tenants/${tenantId}/reject-vacancy`, { admin_note: adminNote }, { headers: getAuthHeader() });
  return response.data;
};

export const recordTenantPayment = async (tenantId, amount, month = null) => {
  const response = await axios.post(
    `${API_BASE}/tenants/${tenantId}/record-payment?amount=${amount}${month ? `&month=${month}` : ''}`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const getTenantDetails = async (tenantId) => {
  const response = await axios.get(`${API_BASE}/tenants/${tenantId}/details`, { headers: getAuthHeader() });
  return response.data;
};

export const getSystemKeys = async () => {
  const response = await axios.get(`${API_BASE}/system/keys`, { headers: getAuthHeader() });
  return response.data;
};

export const getRejectedRegistrations = async (roleFilter = null, limit = 100) => {
  const params = new URLSearchParams();
  if (roleFilter && roleFilter !== 'all') params.append('role_filter', roleFilter);
  params.append('limit', limit);
  const response = await axios.get(`${API_BASE}/rejected-registrations?${params.toString()}`, { headers: getAuthHeader() });
  return response.data;
};

export const getRejectedRegistrationStats = async () => {
  const response = await axios.get(`${API_BASE}/rejected-registrations/stats`, { headers: getAuthHeader() });
  return response.data;
};

// ============ USER APPROVAL & REMOVAL ============
export const getPendingUsers = async () => {
  const response = await axios.get(`${API_BASE}/users/pending`, { headers: getAuthHeader() });
  return response.data;
};

export const approveUser = async (userId, remarks = '') => {
  const response = await axios.patch(
    `${API_BASE}/users/${userId}/approve${remarks ? `?remarks=${encodeURIComponent(remarks)}` : ''}`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const rejectUser = async (userId, reason) => {
  const response = await axios.patch(
    `${API_BASE}/users/${userId}/reject?reason=${encodeURIComponent(reason)}`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const requestVacate = async () => {
  const response = await axios.post(`${API_BASE}/users/request-vacate`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const requestTenantRemoval = async (tenantId) => {
  const response = await axios.post(`${API_BASE}/users/${tenantId}/request-removal`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const confirmRemoval = async (userId, reason) => {
  const response = await axios.patch(
    `${API_BASE}/users/${userId}/confirm-removal?reason=${encodeURIComponent(reason)}`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const deleteUser = async (userId, reason) => {
  const response = await axios.delete(
    `${API_BASE}/users/${userId}?reason=${encodeURIComponent(reason)}`,
    { headers: getAuthHeader() }
  );
  return response.data;
};

// ============ RENT CONFIRMATION (Resident) ============
export const confirmRentPayment = async (paymentId) => {
  const response = await axios.post(`${API_BASE}/rent/confirm/${paymentId}`, {}, { headers: getAuthHeader() });
  return response.data;
};


// ============ ACTIVITY LOGS ============
export const getActivityLogs = async (limit = 50, actionType = null) => {
  let url = `${API_BASE}/activity-logs?limit=${limit}`;
  if (actionType) url += `&action_type=${actionType}`;
  const response = await axios.get(url, { headers: getAuthHeader() });
  return response.data;
};

export const getPendingActions = async () => {
  const response = await axios.get(`${API_BASE}/activity-logs/pending-actions`, { headers: getAuthHeader() });
  return response.data;
};

// ============ OUTSTANDING DUES (REMOVED USERS) ============
export const getOutstandingDuesRemovedUsers = async () => {
  const response = await axios.get(`${API_BASE}/outstanding-dues/removed-users`, { headers: getAuthHeader() });
  return response.data;
};

export const writeOffDue = async (dueType, dueId, note) => {
  const response = await axios.post(`${API_BASE}/outstanding-dues/write-off`, { due_type: dueType, due_id: dueId, note }, { headers: getAuthHeader() });
  return response.data;
};

export const getWrittenOffDues = async () => {
  const response = await axios.get(`${API_BASE}/outstanding-dues/written-off`, { headers: getAuthHeader() });
  return response.data;
};

// ============ REMOVAL HISTORY ============
export const getRemovalHistory = async (roleFilter = null, limit = 50) => {
  let url = `${API_BASE}/removal-history?limit=${limit}`;
  if (roleFilter) url += `&role_filter=${roleFilter}`;
  const response = await axios.get(url, { headers: getAuthHeader() });
  return response.data;
};

export const getRemovalStats = async () => {
  const response = await axios.get(`${API_BASE}/removal-history/stats`, { headers: getAuthHeader() });
  return response.data;
};